import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Mail,
  Calendar,
  Shield,
  Building2,
  Edit,
  Trash2,
  User,
  KeyRound
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import EditUserModal from './EditUserModal';
import CreateUserModal from './CreateUserModal';
import PasswordResetLinkModal from './PasswordResetLinkModal';

interface UserData {
  uid: string;
  email: string;
  name: string;
  lastname: string;
  user_type: 'user' | 'admin' | 'superadmin';
  created_at: any;
  client?: string;
  clients?: string[];
  assigned_clients?: string[];
}

const UsersSection: React.FC = () => {
  const { userData, currentUser } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserData | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData: UserData[] = [];
      
      usersSnapshot.forEach((doc) => {
        usersData.push({
          uid: doc.id,
          ...doc.data()
        } as UserData);
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeColor = (type: string) => {
    switch(type) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserTypeLabel = (type: string) => {
    switch(type) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Administrador';
      default:
        return 'Usuario';
    }
  };

  const handleEditUser = (user: UserData) => {
    setEditingUser(user);
    setEditModalOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      try {
        // Unificado en approach REST (antes usaba deleteDoc directo a Firestore)
        const token = await currentUser?.getIdToken();
        await axios.delete(`/api/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchUsers();
      } catch (error: any) {
        console.error('Error deleting user:', error);
        alert(error?.response?.data?.error || 'Error al eliminar el usuario');
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || user.user_type === filterType;

    return matchesSearch && matchesType;
  });

  return (
    <>
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar usuarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos los tipos</option>
            <option value="user">Usuarios</option>
            <option value="admin">Administradores</option>
            <option value="superadmin">Super Admin</option>
          </select>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.uid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name} {user.lastname}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getUserTypeColor(user.user_type)}`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {getUserTypeLabel(user.user_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                        {user.assigned_clients && user.assigned_clients.length > 0
                          ? `${user.assigned_clients.length} cliente${user.assigned_clients.length > 1 ? 's' : ''}`
                          : user.clients && user.clients.length > 0
                          ? `${user.clients.length} cliente${user.clients.length > 1 ? 's' : ''}`
                          : user.client || 'Sin asignar'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {user.created_at?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        {isSuperadmin && (
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-1.5 rounded text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            title="Editar usuario"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {isSuperadmin && (
                          <button
                            onClick={() => setResetTarget(user)}
                            className="p-1.5 rounded text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                            title="Generar link de reset de password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        {isSuperadmin && (
                          <button
                            onClick={() => handleDeleteUser(user.uid)}
                            className="p-1.5 rounded text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                            title="Eliminar usuario"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredUsers.length === 0 && !loading && (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No se encontraron usuarios
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm 
                ? 'Intenta con otros términos de búsqueda'
                : 'Comienza agregando un nuevo usuario'}
            </p>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={() => {
          fetchUsers();
          setCreateModalOpen(false);
        }}
      />

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingUser(null);
          }}
          onSave={() => {
            fetchUsers();
            setEditModalOpen(false);
            setEditingUser(null);
          }}
        />
      )}

      {/* Password Reset Link Modal (reusable) */}
      <PasswordResetLinkModal
        user={resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </>
  );
};

export default UsersSection;